<template>
  <VDataGridV2
      :loader="dataLoader"
      :context-menu="OrdersService.positionsContextMenu"
      paginated
      entity-type="orderPosition"
      :selectable="selectableMode"
      :selected-rows="selectedPositions"
      @row:dblclick="openPositionCard"
      @row:selected="onRowSelected"
      @row:deselected="onRowDeselected"
      @update:selected-rows="onSelectedRowsUpdate"
  >
    <VDataGridColumnV2
        v-if="isColumnVisible('comment') && (isAdminMode || $core.currentPartner.isBuyer)"
        id="comment"
        content-class="comment"
    >
      <template #default="{ row }">
        <CommentsButton entity-type-name="orderPosition" :entity="row"
                        v-if="$core.currentPartner.isBuyer === true"/>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('id')"
        id="id"
        width="100px"
        sortable
        sort-default-value="desc"
        filterable
        filter-send-name="idPattern"
        :filter-props="{ placeholder: '№', clearable: true }"
    >
      <template #label>
        №
        <div class="nowrap description">Заказ №</div>
        <div class="nowrap description">Создан</div>
      </template>
      <template #default="{ row }">
        <VLink @click.stop="openPositionCard(row)">
          {{ $core.utils.string.formatWithDelimiter(row.id) }}
        </VLink>
        <div v-if="row.order?.id" class="description">
          {{ $core.utils.string.formatWithDelimiter(row.order.id) }}
        </div>
        <div class="nowrap description">
          {{ $core.utils.date.format(row.createdAt, 'DD.MM.yyyy') }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="showSellerColumn && isColumnVisible('seller')"
        id="seller"
        width="150px"
        content-class="ellipsis"
        filterable
        filter-identity="*"
        filter-type="select"
        filter-send-name="seller"
        :filter-props="{ searchFunction: PartnersService.searchSellers, placeholder: 'Название партнера', clearable: true }"
    >
      <template #label>
        Грузоотправитель
        <div class="description">Юр лицо</div>
      </template>
      <template #default="{ row }">
        <div class="ellipsis" :title="row.seller.name">
          {{ row.seller.name }}
        </div>
        <div class="description ellipsis" :title="row.sellerCompany.name">
          {{ row.sellerCompany.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="showBuyerColumn && isColumnVisible('buyer')"
        id="buyer"
        width="150px"
        content-class="ellipsis"
        filterable
        filter-identity="*"
        filter-type="select"
        filter-send-name="buyer"
        :filter-props="{ searchFunction: PartnersService.searchBuyers, placeholder: 'Название партнера', clearable: true }"
    >
      <template #label>
        Грузополучатель
        <div class="description">Юр лицо</div>
      </template>
      <template #default="{ row }">
        <div class="ellipsis" :title="row.buyer.name">
          {{ row.buyer.name }}
        </div>
        <div class="description ellipsis" :title="row.buyerCompany.name">
          {{ row.buyerCompany.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('userCode')"
        id="userCode"
        label="Арт"
        width="80px"
        content-align="center"
        content-class="ellipsis"
        filterable
        filter-send-name="codePattern"
        :filter-props="{ placeholder: 'Артикул', searchable: true, clearable: true }"
    >
      <template #default="{ row }">
        <VLink style="width: 100%" class="ellipsis" @click="OrdersService.showCodeInfoPopup(row, $event.currentTarget)">{{ row.code || '--' }}</VLink>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('product')"
        id="product"
        content-class="ellipsis"
        width="250px"
        filterable
        filter-identity="*"
        filter-type="select"
        filter-send-name="product"
        :filter-props="{ placeholder: 'Название или артикул товара', dataUrl: 'products.getList', searchable: true }"
        edit-material="info_outline"
        @edit="(row, anchor) => OrdersService.showPositionInfoPopup(row, anchor)"
    >
      <template #label>
        Товар
        <div class="description ellipsis">Производитель / материал</div>
      </template>
      <template #default="{ row }">
        {{ row.name }}
        <div v-if="row.isPrinting && row.material" class="description ellipsis" :title="row.material">
          Материал: {{ row.material }}
        </div>
        <div v-else-if="row.isRawMaterialsAndPackage && row.manufacturer" class="description ellipsis" :title="row.manufacturer.name">
          Производитель: {{ row.manufacturer.name }}
        </div>
        <VBadges>
          <VBadge v-if="!row.isOfferBased" class="primary">Прямой</VBadge>
          <VBadge v-if="row.isSamples">Образцы</VBadge>
          <VBadge v-if="row.isSamples && row.isForTestsOnly">Не под план</VBadge>
          <VBadge v-if="row.isDraft">Черновик</VBadge>
        </VBadges>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('qty') && canViewSellerData"
        id="qty"
        width="140px"
        content-align="right"
        label="Кол-во"
    >
      <template #default="{ row }"> {{ row.qty.format(row.unit) }} </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('sum') && canViewSellerData"
        id="sumSeller"
        width="140px"
        content-align="right"
    >
      <template #label>
        Цена
        <div class="description">Сумма</div>
      </template>
      <template #default="{ row }">
        {{ row.priceFullSeller.format(row.currency, row.unit) }}
        <div class="description">{{ row.sumFullSeller.format(row.currency) }}</div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('sum') && canViewBuyerData"
        id="sumBuyer"
        width="140px"
        content-align="right"
    >
      <template #label>
        Цена покупателя
        <div class="description">Сумма</div>
      </template>
      <template #default="{ row }">
        {{ row.priceFullBuyer?.format(row.currency, row.unit) ?? '--' }}
        <div class="description">{{ row.sumFullBuyer?.format(row.currency) ?? '--' }}</div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('documentNumber') && showStatusBarColumns"
        id="documentNumber"
        label="Отгрузка"
        width="160px"
        content-class="form"
        filterable
        filter-send-name="shipmentDocumentNumber"
        :filter-props="{ placeholder: 'Номер документа', clearable: true }"
    >
      <template #default="{ row }">
        <ShipmentStatusBar
            :shipment-status="row.shipmentStatus"
            :unit="row.unit"
            @open="(anchor) => OrdersService.showPositionShipmentsPopup(row, anchor)"
        />
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('desiredReceiptDate')"
        id="desiredReceiptDate"
        label="Доставка, план"
        width="140px"
        header-class="nowrap"
        content-class="nowrap"
        content-align="center"
        sortable
        filterable
        filter-type="select"
        filter-send-name="isReceived"
        :filter-props="{
        placeholder: 'Доставлен/нет',
         options: [
           { id: true, name: 'Доставлен' },
           { id: false, name: 'Не доставлен' },
         ],
         clearable: true
      }"
    >
      <template #default="{ row }">
        {{ $core.utils.date.humanizeDate(row.desiredReceiptDate, null) }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('plannedShipmentDate') "
        id="plannedShipmentDate"
        width="140px"
        content-align="center"
        content-class="ellipsis"
        sortable
        filterable
        filter-type="select"
        filter-send-name="isShipped"
        :filter-props="{
        placeholder: 'Отгружен/нет',
        clearable: true,
        options: [
          { id: true, name: 'Отгружен' },
          { id: false, name: 'Не отгружен' }
        ],
      }"
    >
      <template #label>
        <div class="nowrap">Отгрузка, план</div>
        <div class="nowrap description">Тип доставки</div>
      </template>
      <template #default="{ row }">
        <div> {{ $core.utils.date.humanizeDate(row.plannedShipmentDate, null) }}</div>
        <div class="description ellipsis">{{ row.deliveryType.name }}</div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('payment') && showStatusBarColumns"
        id="payment"
        label="Оплата"
        width="180px"
        content-class="form"
    >
      <template #default="{ row }">
        <PaymentStatusBar
            :payment-status="row.paymentStatus"
            :currency="row.currency"
            @open="(anchor) => OrdersService.showPositionPaymentsPopup(row, anchor)"
        />
      </template>
    </VDataGridColumnV2>

    <VDataGridActionBarV2 v-if="showActionBar">
      <template #default="{ selectedRows }">
        <VButton
            v-if="!selectableMode"
            theme="primary"
            :disabled="!dataLoader.data?.length"
            @click="startSelect()"
        >
          Выбрать позиции
        </VButton>

        <template v-else>
          <VLink @click="clearSelectedPositions">Отмена</VLink>
          <div class="nowrap">
            Выбрано {{ selectedRows.length }} строк{{ $core.utils.regional.ru.pluralize(selectedRows.length, 'а', 'и', '') }}
          </div>

          <VButton
              theme="primary"
              :disabled="assemblyDisabledError !== null && selectedRows.length > 0"
              :title="assemblyDisabledError"
              @click="createAssemblyFromSelection"
          >
            Создать сборочный лист
          </VButton>

          <VButton
              theme="primary"
              :disabled="shipmentDisabledError !== null && selectedRows.length > 0"
              :title="shipmentDisabledError"
              @click="createShipmentFromSelection"
          >
            Создать отгрузку
          </VButton>
        </template>
      </template>
    </VDataGridActionBarV2>
  </VDataGridV2>
</template>

<script lang="ts">
import Order from "@/domains/Orders/Order";

import OrdersService           from '@/domains/Orders/OrdersService';
import AssembliesService       from '@/domains/Assemblies/AssembliesService';
import ShipmentsService        from '@/domains/Shipments/ShipmentsService';
import OrderPosition           from '@/domains/Orders/OrderPosition';
import Preorder                from '@/domains/Preorders/Preorder';
import PreorderPosition        from '@/domains/Preorders/PreorderPosition';
import PreordersService        from '@/domains/Preorders/PreordersService';
import Assembly                from '@/domains/Assemblies/Assembly';
import AssemblyPosition        from '@/domains/Assemblies/AssemblyPosition';
import Shipment                from '@/domains/Shipments/Shipment';
import ShipmentPosition        from '@/domains/Shipments/ShipmentPosition';
import PartnersService         from '@/domains/Partners/PartnersService';
import DataGridLoader          from '@/lib/components/VDataGridV2/DataGridLoader';
import PersonsService          from "@/domains/Persons/PersonsService";
import ShipmentStatusBar       from '@/domains/Orders/components/ShipmentStatusBar/ShipmentStatusBar.vue';
import PaymentStatusBar        from '@/domains/Orders/components/PaymentStatusBar/PaymentStatusBar.vue';

export default defineComponent({
  name: 'OrderPositionsRegistry',

  components: {
    ShipmentStatusBar,
    PaymentStatusBar,
  },

  props: {
    /**
     * Админский режим — админские колонки и isAdminMode на бэк.
     * Обычно Включается только из роутера (props: { adminMode: true }) для админского реестра.
     * При встраивании в воронки/карточки не указывается — реестр работает в регулярном режиме.
     */
    adminMode: {
      type:    Boolean as PropType<Boolean>,
      default: false,
    },
    /**
     * Состояние позиций. id одного из OrdersService.States.
     * Маппится в бэк-флаг (isInWork / isFinished / isReceived / isDeleted).
     * Без значения — все состояния.
     */
    state: {
      type:    String as PropType<typeof OrdersService.States[number]['id']>,
      default: null,
    },

    showActionBar: {
      type: Boolean,
      default: false
    },

    /**
     * Наполняемый документ. Если передан — выбор позиций в реестре
     * обогащает targetDocument.positions через его addPosition/deletePosition.
     * Поддерживаются Order / Assembly / Shipment — у всех трёх единый
     * контракт positions / addPosition / deletePosition.
     */
    targetDocument: {
      type: Object as PropType<Order | Assembly | Shipment>,
      default: null,
    },

    /**
     * Фильтры реестра. isPreorder === true переключает реестр на позиции
     * предзаказов: другой сервис загрузки и другой класс строк.
     */
    params: {
      type: Object as PropType<
        (Parameters<typeof OrdersService.getPositionsListPaginated>[0]
          | Parameters<typeof PreordersService.getListPaginated>[0])
        & { isPreorder?: boolean }
      >,
      default: null,
    },

    hiddenColumns: {
      type: Array as PropType<string[]>,
      default: () => []
    }
  },

  emits: ['positions:updated'],

  expose: ['refresh'],

  setup() {
    return {
      PartnersService,
      OrdersService,
    }
  },

  data() {
    /** Позиции предзаказов грузит свой сервис — он же гидрирует их в PreorderPosition */
    let fetchFunction: typeof OrdersService.getPositionsListPaginated | typeof PreordersService.getListPaginated;

    if (this.params?.isPreorder === true) {
      fetchFunction = PreordersService.getListPaginated;
    }
    else {
      fetchFunction = OrdersService.getPositionsListPaginated;
    }

    return {
      selectable: false,
      selectedPositionsLocal: [] as (OrderPosition | PreorderPosition)[],

      dataLoader: new DataGridLoader(fetchFunction)
          .useExtraParams({
            /** state → бэк-флаг (isInWork / isFinished / isReceived / isDeleted) */
            ...OrdersService.getStateFlag(this.state),
            requireCommentsInfo: true,
          })
          .useReactiveSource(() => {
            const result: Record<string, any> = {
              ...this.params,
              isAdminMode: this.isAdminMode,
            };

            if (this.params?.isPreorder === true) {
              // у предзаказов флага isDraft нет — не шлём
            }
            else {
              result.isDraft = false;
            }

            return result;
          }),

      responsibleFilterProps: { searchFunction: (pattern: string) => PersonsService.searchPersons(pattern), placeholder: 'Имя, email или id', clearable: true },
    };
  },

  computed: {
    /**
     * Админский режим — только если включён пропсом (роутер) и зритель действительно админ.
     */
    isAdminMode(): boolean {
      return this.adminMode && this.$core.currentUser.isAdmin;
    },

    /** Админ видит обе колонки; обычный продавец свою колонку не видит */
    showSellerColumn(): boolean {
      return this.isAdminMode || this.$core.currentPartner.isSeller === false;
    },

    /** Симметрично — обычный покупатель свою колонку не видит */
    showBuyerColumn(): boolean {
      return this.isAdminMode || this.$core.currentPartner.isBuyer === false;
    },

    /** Право видеть данные стороны продавца */
    canViewSellerData(): boolean {
      if (this.isAdminMode === true) return true;
      if (this.$core.currentPartner.isSeller === true) return true;
      if (this.$core.currentPartner.isLSZ === true) return true;

      return false;
    },

    /** Право видеть данные стороны покупателя. ЛСЗ сюда не попадает — он смотрит цену продавца */
    canViewBuyerData(): boolean {
      if (this.isAdminMode === true) return true;
      if (this.$core.currentPartner.isBuyer === true && this.$core.currentPartner.isLSZ === false) return true;

      return false;
    },

    /** Колонки с диаграммами оплаты и отгрузки — только админу или ЛСЗ */
    showStatusBarColumns(): boolean {
      return this.$core.currentPartner.isPrimaryBuyer === true || this.$core.currentUser.isAdmin === true;
    },

    assemblyDisabledError(): string | null {
      return OrdersService.reasonUnavailableCreateAssembly(this.selectedPositions);
    },

    shipmentDisabledError(): string | null {
      return OrdersService.reasonUnavailableCreateShipment(this.selectedPositions);
    },

    hiddenColumnsSet(): Set<string> {
      return new Set(this.hiddenColumns);
    },

    /**
     * Реестр работает в режиме наполнения документа, если targetDocument передан.
     * В этом режиме selectable всегда включён и выбор пишется в targetDocument.positions.
     */
    hasDocument(): boolean {
      return this.targetDocument !== null;
    },

    /**
     * Выбор включён всегда при наличии документа, иначе по кнопке (selectable).
     */
    selectableMode(): boolean {
      if (this.hasDocument === true) return true;
      return this.selectable;
    },

    /**
     * Выбранные строки.
     *
     * Грид сопоставляет выбранное с данными по id строки. В заказ позиция
     * уходит копией со сброшенной идентичностью, поэтому отмеченными
     * показываем строки самого реестра, из которых копии сделаны, а связь
     * держим через sourcePositionId.
     *
     * Сборка и отгрузка собирают из строки свою позицию (fromOrderPosition) —
     * связь со строкой реестра держим через orderPosition.
     */
    selectedPositions(): (OrderPosition | PreorderPosition)[] {
      if (this.hasDocument === true) {
        if (this.targetDocument instanceof Order) {
          const sourceIds = new Set(
              this.targetDocument.positions.map(_position => _position.sourcePositionId),
          );

          return (this.dataLoader.data ?? []).filter(_row => sourceIds.has(_row.id));
        } else {
          const orderPositionIds = new Set(
              this.targetDocument.positions.map(_position => _position.orderPosition.id),
          );

          return (this.dataLoader.data ?? []).filter(_row => orderPositionIds.has(_row.id));
        }
      } else {
        return this.selectedPositionsLocal;
      }
    },
  },

  methods: {
    async openPositionCard(row: OrderPosition | PreorderPosition): Promise<void> {

      const submited = await this.$core.dialogs.OrderPositionCard.showAsPromise({
        id: row.id,
        isPreorder: row.isPreorder,
        isAdminMode: this.isAdminMode
      })

      if (submited) {
        this.dataLoader.refresh();
      }
    },

    /** Включить режим выбора, опционально сразу с одной позицией (вызов из контекстного меню) */
    startSelect(position?: OrderPosition | PreorderPosition): void {
      this.selectable = true;
      if (position) {
        this.selectedPositionsLocal.push(position);
      }
    },

    clearSelectedPositions(): void {
      this.selectedPositionsLocal = [];
      this.selectable = false;
    },

    /**
     * Колбэк выбора строки. В режиме документа добавляем в targetDocument,
     * в обычном режиме набор ведёт грид через update:selected-rows.
     */
    onRowSelected(position: OrderPosition | PreorderPosition): void {
      if (this.hasDocument === true) {
        let documentPosition: OrderPosition | PreorderPosition | AssemblyPosition | ShipmentPosition;

        // Preorder наследует Order — проверяем первым
        if (this.targetDocument instanceof Preorder) {
          // реестр предзаказа грузит строки через PreordersService — это PreorderPosition
          documentPosition = PreorderPosition.fromPreorderPosition(position as PreorderPosition);
          documentPosition.setOrder(this.targetDocument);
        }
        else if (this.targetDocument instanceof Order) {
          documentPosition = OrderPosition.fromOrderPosition(position as OrderPosition);
          documentPosition.setOrder(this.targetDocument);
        }
        else if (this.targetDocument instanceof Assembly) {
          documentPosition = AssemblyPosition.fromOrderPosition(position as OrderPosition);
        }
        else if (this.targetDocument instanceof Shipment) {
          documentPosition = ShipmentPosition.fromOrderPosition(position as OrderPosition);
        }
        else {
          documentPosition = position;
        }

        this.targetDocument.addPosition(documentPosition);
      }
    },

    /**
     * Снятие отметки. В заказе лежит копия строки, а не она сама —
     * ищем копию по sourcePositionId.
     */
    onRowDeselected(position: OrderPosition | PreorderPosition): void {
      if (this.hasDocument === true) {
        if (this.targetDocument instanceof Order) {
          const added = this.targetDocument.positions.find(
              _position => _position.sourcePositionId === position.id,
          );

          if (added === undefined) {
            // копии этой строки в заказе нет
          } else {
            this.targetDocument.removePosition(added);
          }
        } else {
          /** Сборка и отгрузка тоже держат не саму строку, а свою позицию,
           *  собранную из неё, — ищем по orderPosition */
          const added = this.targetDocument.positions.find(
              _position => _position.orderPosition.id === position.id,
          );

          if (added === undefined) {
            // позиции из этой строки в документе нет
          } else {
            this.targetDocument.removePosition(added);
          }
        }
      }
    },

    onSelectedRowsUpdate(rows: (OrderPosition | PreorderPosition)[]): void {
      if (this.hasDocument === true) return;
      this.selectedPositionsLocal = rows;
    },


    async createAssemblyFromSelection(): Promise<void> {
      const positions = this.selectedPositions;

      this.clearSelectedPositions();

      await AssembliesService.showAssemblyCard(positions);
      this.dataLoader.refresh();
      this.$emit('positions:updated');
    },

    async createShipmentFromSelection(): Promise<void> {
      const positions = this.selectedPositions;

      this.clearSelectedPositions();

      await ShipmentsService.showShipmentCard(positions);
      this.dataLoader.refresh();
      this.$emit('positions:updated');
    },

    isColumnVisible(columnId: string): boolean {
      return this.hiddenColumnsSet.has(columnId) === false;
    },

    refresh(): void {
      this.dataLoader.refresh();
    },
  },
});
</script>